import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar, Clock, User, CheckCircle2, XCircle, Loader2, Trash2, BookOpen } from 'lucide-react';

interface Booking {
  id: number;
  slot_id: number;
  parent_name: string;
  parent_email: string;
  student_name: string;
  student_id: string;
  status: 'pending_confirmation' | 'confirmed' | 'cancelled' | 'completed';
  confirmation_token: string;
  cancellation_token: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  notes: string | null;
  start_time: string;
  end_time: string;
  config_title: string;
  appointment_type: string;
  teacher_first_name: string;
  teacher_last_name: string;
}

interface Teacher {
  config_id: number;
  title: string;
  description: string;
  appointment_type: string;
  slot_duration_minutes: number;
  public_token: string;
  teacher_id: number;
  username?: string;
  first_name: string;
  last_name: string;
  teacher_display_name?: string;
  available_slots_count: number;
}

interface Slot {
  id: number;
  config_id: number;
  start_time: string;
  end_time: string;
  status: string;
  config_title: string;
  slot_duration_minutes: number;
  teacher_id: number;
  teacher_first_name: string;
  teacher_last_name: string;
}

export default function StudentAppointments() {
  const queryClient = useQueryClient();
  const [selectedTeachers, setSelectedTeachers] = useState<number[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    parent_name: '',
    parent_email: '',
    student_name: '',
    notes: ''
  });

  // Fetch student's bookings
  const { data: bookings, isLoading: bookingsLoading } = useQuery<Booking[]>(
    ['appointments', 'student', 'bookings'],
    async () => {
      const response = await api.get('/appointments/student/bookings');
      return response.data.data;
    }
  );

  // Fetch available teachers
  const { data: teachers, isLoading: teachersLoading } = useQuery<Teacher[]>(
    ['appointments', 'student', 'teachers'],
    async () => {
      const response = await api.get('/appointments/student/teachers');
      return response.data.data;
    }
  );

  // Fetch slots for selected teachers
  const { data: slots, isLoading: slotsLoading } = useQuery<Slot[]>(
    ['appointments', 'student', 'slots', selectedTeachers.join(',')],
    async () => {
      if (selectedTeachers.length === 0) return [];
      const response = await api.get(`/appointments/student/teachers/${selectedTeachers.join(',')}/slots`);
      return response.data.data;
    },
    { enabled: selectedTeachers.length > 0 }
  );

  // Erlaubte E-Mail-Adressen für das Kind (nur diese sind bei Buchung gültig)
  const { data: allowedEmailsData } = useQuery<{ allowedEmails: string[] }>(
    ['appointments', 'student', 'allowed-emails'],
    async () => {
      const response = await api.get('/appointments/student/allowed-emails');
      return response.data.data;
    },
    { enabled: showBookingForm }
  );
  const allowedEmails = allowedEmailsData?.allowedEmails ?? [];

  // Cancel booking mutation (sendet E-Mail an Eltern; Storno wird erst nach Bestätigung wirksam)
  const cancelBookingMutation = useMutation(
    async (bookingId: number) => {
      const booking = bookings?.find(b => b.id === bookingId);
      if (!booking?.cancellation_token) {
        throw new Error('Stornierungs-Token nicht gefunden');
      }
      const res = await api.post(`/appointments/public/cancel/${booking.cancellation_token}`);
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['appointments', 'student', 'bookings']);
        toast.success(data?.message || 'E-Mail zur Storno-Bestätigung wurde versendet.');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Stornierung');
      }
    }
  );

  // Book appointments mutation
  const bookMutation = useMutation(
    async (data: { slot_ids: number[]; parent_name: string; parent_email: string; student_name: string; notes?: string }) => {
      const response = await api.post('/appointments/student/book', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['appointments', 'student', 'bookings']);
        queryClient.invalidateQueries(['appointments', 'student', 'slots']);
        setSelectedTeachers([]);
        setSelectedSlots([]);
        setShowBookingForm(false);
        setBookingForm({ parent_name: '', parent_email: '', student_name: '', notes: '' });
        toast.success(data.data?.message || 'Termine erfolgreich gebucht');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Buchung');
      }
    }
  );

  // Group slots by time (nur zukünftige Termine)
  const groupedSlots = useMemo(() => {
    if (!slots || slots.length === 0) return {};
    const now = new Date();

    const grouped: Record<string, Record<number, Slot[]>> = {};

    slots.forEach(slot => {
      const start = parseISO(slot.start_time);
      if (start < now) return; // vergangene Termine ausblenden
      const timeKey = format(start, 'yyyy-MM-dd HH:mm');
      if (!grouped[timeKey]) {
        grouped[timeKey] = {};
      }
      if (!grouped[timeKey][slot.config_id]) {
        grouped[timeKey][slot.config_id] = [];
      }
      grouped[timeKey][slot.config_id].push(slot);
    });

    return grouped;
  }, [slots]);

  // Get unique time slots sorted
  const timeSlots = useMemo(() => {
    return Object.keys(groupedSlots).sort();
  }, [groupedSlots]);

  const handleTeacherToggle = (configId: number) => {
    setSelectedTeachers(prev => {
      if (prev.includes(configId)) {
        // Remove teacher and all their selected slots
        const teacher = teachers?.find(t => t.config_id === configId);
        if (teacher) {
          const teacherSlots = slots?.filter(s => s.config_id === configId) || [];
          setSelectedSlots(prevSlots => 
            prevSlots.filter(slotId => !teacherSlots.some(s => s.id === slotId))
          );
        }
        return prev.filter(id => id !== configId);
      } else {
        return [...prev, configId];
      }
    });
  };

  const handleSlotToggle = (slotId: number, configId: number) => {
    setSelectedSlots(prev => {
      // Remove any existing slot for this teacher
      const teacherSlots = slots?.filter(s => s.config_id === configId) || [];
      const filtered = prev.filter(id => !teacherSlots.some(s => s.id === id));
      
      // Add the new slot if it's not already selected
      if (!prev.includes(slotId)) {
        return [...filtered, slotId];
      }
      // If clicking the same slot, deselect it
      return filtered;
    });
  };

  // Get selected slot for a specific teacher
  const getSelectedSlotForTeacher = (configId: number) => {
    const teacherSlots = slots?.filter(s => s.config_id === configId) || [];
    return selectedSlots.find(slotId => teacherSlots.some(s => s.id === slotId));
  };

  const handleBook = () => {
    if (selectedSlots.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Termin aus');
      return;
    }
    if (!bookingForm.parent_name || !bookingForm.parent_email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    bookMutation.mutate({
      slot_ids: selectedSlots,
      parent_name: bookingForm.parent_name,
      parent_email: bookingForm.parent_email,
      student_name: bookingForm.student_name,
      notes: bookingForm.notes || undefined
    });
  };

  const selectedTeachersData = useMemo(() => {
    return teachers?.filter(t => selectedTeachers.includes(t.config_id)) || [];
  }, [teachers, selectedTeachers]);

  /** Anzeigename: Angezeigter Name (Einstellungen) → Vorname + Nachname → Username */
  const getTeacherDisplayName = (teacher: Teacher) => {
    if (teacher.teacher_display_name?.trim()) return teacher.teacher_display_name.trim();
    const name = [teacher.first_name, teacher.last_name].filter(Boolean).join(' ').trim();
    return name || teacher.username || 'Unbekannt';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-50">Terminbuchungen</h1>

      {/* My Bookings Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-50">Meine gebuchten Termine</h2>
        {bookingsLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          </div>
        ) : bookings && bookings.length > 0 ? (
          <div className="space-y-3">
            {bookings.map(booking => (
              <div
                key={booking.id}
                className={`p-4 rounded-lg border-2 ${
                  booking.status === 'confirmed'
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/40'
                    : booking.status === 'pending_confirmation'
                    ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/40'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-lg">
                      {booking.teacher_first_name} {booking.teacher_last_name}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mt-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {format(parseISO(booking.start_time), 'dd.MM.yyyy', { locale: de })}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4" />
                        {format(parseISO(booking.start_time), 'HH:mm', { locale: de })} -{' '}
                        {format(parseISO(booking.end_time), 'HH:mm', { locale: de })}
                      </div>
                    </div>
                    <div className="mt-2">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          booking.status === 'confirmed'
                            ? 'bg-green-200 text-green-800 dark:bg-green-900/60 dark:text-green-200'
                            : booking.status === 'pending_confirmation'
                            ? 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-200'
                            : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {booking.status === 'confirmed'
                          ? 'Bestätigt'
                          : booking.status === 'pending_confirmation'
                          ? 'Wartet auf Bestätigung'
                          : booking.status}
                      </span>
                    </div>
                  </div>
                  {booking.status === 'confirmed' && booking.cancellation_token && (
                    <button
                      onClick={() => {
                        if (confirm('Stornierung anfordern? An die hinterlegte E-Mail-Adresse wird ein Bestätigungslink gesendet. Die Stornierung wird erst nach Klick auf den Link wirksam (gültig 30 Min.).')) {
                          cancelBookingMutation.mutate(booking.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-800 flex items-center gap-1"
                      disabled={cancelBookingMutation.isLoading}
                    >
                      <Trash2 className="w-4 h-4" />
                      Stornieren
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 dark:text-gray-500">Noch keine Termine gebucht</div>
        )}
      </div>

      {/* Teacher Selection Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-50">Neue Termine buchen</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Lehrer auswählen:</label>
          {teachersLoading ? (
            <div className="text-center py-4">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
            </div>
          ) : teachers && teachers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {teachers.map(teacher => (
                <label
                  key={teacher.config_id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedTeachers.includes(teacher.config_id)
                      ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/40'
                      : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedTeachers.includes(teacher.config_id)}
                      onChange={() => handleTeacherToggle(teacher.config_id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-semibold">
                        {getTeacherDisplayName(teacher)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">{teacher.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
                        {teacher.available_slots_count} verfügbare Termine
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 dark:text-gray-500">Keine Lehrer mit verfügbaren Terminen</div>
          )}
        </div>
      </div>

      {/* Slot Selection Table */}
      {selectedTeachers.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-50">Verfügbare Termine auswählen</h3>
          {slotsLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            </div>
          ) : slots && slots.length > 0 && timeSlots.length > 0 ? (
            <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-600 text-white shadow-md">
                  <tr>
                    <th className="border border-slate-500 p-3 text-left font-semibold min-w-[140px]">
                      Zeit
                    </th>
                    {selectedTeachersData.map(teacher => (
                      <th
                        key={teacher.config_id}
                        className="border border-slate-500 p-3 text-center font-semibold min-w-[120px]"
                        title={`Termine von ${getTeacherDisplayName(teacher)}`}
                      >
                        {getTeacherDisplayName(teacher)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {timeSlots.map(timeKey => {
                    const timeSlot = parseISO(timeKey);
                    return (
                      <tr key={timeKey}>
                        <td className="border border-gray-200 dark:border-gray-700 p-2 font-semibold text-gray-900 dark:text-gray-100">
                          {format(timeSlot, 'dd.MM.yyyy HH:mm', { locale: de })}
                        </td>
                        {selectedTeachersData.map(teacher => {
                          const teacherSlots = groupedSlots[timeKey]?.[teacher.config_id] || [];
                          const selectedSlotForTeacher = getSelectedSlotForTeacher(teacher.config_id);
                          return (
                            <td key={teacher.config_id} className="border border-gray-200 dark:border-gray-700 p-2 text-center">
                              {teacherSlots.length > 0 ? (
                                teacherSlots.map(slot => {
                                  const isSelected = selectedSlotForTeacher === slot.id;
                                  return (
                                    <div
                                      key={slot.id}
                                      onClick={() => handleSlotToggle(slot.id, teacher.config_id)}
                                      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 mb-2 ${
                                        isSelected
                                          ? 'bg-green-500 text-white shadow-md scale-105'
                                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200'
                                      }`}
                                    >
                                      <div className="font-semibold">
                                        {format(parseISO(slot.start_time), 'HH:mm', { locale: de })}-{format(parseISO(slot.end_time), 'HH:mm', { locale: de })}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 dark:text-gray-500">
              {slots && slots.length > 0 && timeSlots.length === 0
                ? 'Keine zukünftigen Termine für die ausgewählten Lehrer.'
                : 'Keine verfügbaren Termine für die ausgewählten Lehrer'}
            </div>
          )}

          {selectedSlots.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowBookingForm(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
              >
                <BookOpen className="w-5 h-5" />
                Jetzt buchen ({selectedSlots.length} Termin{selectedSlots.length > 1 ? 'e' : ''})
              </button>
            </div>
          )}
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-50">Buchungsdetails</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-4">
              Die Bestätigung und ggf. Storno-Links werden an die angegebene E-Mail-Adresse gesendet. Es sind nur E-Mail-Adressen erlaubt, die für das Kind bei der Schule hinterlegt sind.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name des Elternteils <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bookingForm.parent_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, parent_name: e.target.value })}
                  className="input-field w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-Mail-Adresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={bookingForm.parent_email}
                  onChange={(e) => setBookingForm({ ...bookingForm, parent_email: e.target.value })}
                  className="input-field w-full"
                  required
                />
                {allowedEmails.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                    Erlaubte Adressen: {allowedEmails.join(', ')}
                  </p>
                )}
                {allowedEmails.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    Für das Kind sind keine E-Mail-Adressen hinterlegt. Bitte an die Schule wenden.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name des Schülers</label>
                <input
                  type="text"
                  value={bookingForm.student_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, student_name: e.target.value })}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notizen</label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleBook}
                  disabled={bookMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {bookMutation.isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Wird gebucht...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Buchen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

